import { useServices } from '@/app/providers';
import { ActivityFeed } from '@/presentation/components/organisms/admin-v2/ActivityFeed';
import { AlertList } from '@/presentation/components/organisms/admin-v2/AlertList';
import { CommunityCard } from '@/presentation/components/organisms/admin-v2/CommunityCard';
import { FinanzasOverview } from '@/presentation/components/organisms/admin-v2/FinanzasOverview';
import { InsightHeroCard } from '@/presentation/components/organisms/admin-v2/InsightHeroCard';
import { KpiCard } from '@/presentation/components/organisms/admin-v2/KpiCard';
import { LockedDashboardCard } from '@/presentation/components/organisms/admin-v2/LockedDashboardCard';
import { PendingReservationsCard } from '@/presentation/components/organisms/admin-v2/PendingReservationsCard';
import {
  PendingPaymentsCard,
  PENDING_PAYMENTS_ALERT_ID,
} from '@/presentation/components/organisms/admin-v2/PendingPaymentsCard';
import { TodayAccessCard } from '@/presentation/components/organisms/admin-v2/TodayAccessCard';
import { useDashboardData } from '@/modules/admin-v2/hooks/useDashboardData';
import { formatGreeting } from '@/shared/utils/greeting';

function formatMXN(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return `$${amount.toLocaleString('es-MX')}`;
}

export function AdminDashboardV2() {
  const { currentUser } = useServices();
  const greeting = currentUser ? formatGreeting(currentUser.name) : 'Bienvenido';
  const { data, loading, error, permissions } = useDashboardData();

  const { aging } = data;
  const moraTotal = aging?.totalAmount ?? 0;
  const moraUnits = aging?.totalUnits ?? 0;
  const occupancy = data.kpis.find((k) => k.key === 'occupancy');
  const security = data.kpis.find((k) => k.key === 'security_streak');
  const tickets = data.kpis.find((k) => k.key === 'open_tickets');

  const kpis = [
    {
      key: 'ocupacion',
      visible: permissions.canReadUnit,
      title: 'Ocupación',
      node: (
        <KpiCard
          title="Ocupación"
          value={loading.kpis ? '—' : occupancy?.value ?? '—'}
          subValue={occupancy?.subValue ?? ''}
        />
      ),
    },
    {
      key: 'seguridad',
      visible: permissions.canReadAccessLog,
      title: 'Seguridad',
      node: (
        <KpiCard
          title="Seguridad"
          value={loading.kpis ? '—' : security?.value ?? '—'}
          subValue={security?.subValue ?? ''}
        />
      ),
    },
    {
      key: 'reportes',
      visible: permissions.canReadTicket,
      title: 'Reportes abiertos',
      node: (
        <KpiCard
          title="Reportes abiertos"
          value={loading.kpis ? '—' : tickets?.value ?? '—'}
          subValue={tickets?.subValue ?? ''}
          delta={tickets?.deltaLabel}
          deltaPositive={tickets?.deltaPositive}
        />
      ),
    },
    {
      key: 'mora',
      visible: permissions.canReadCharge,
      title: 'Mora acumulada',
      node: (
        <KpiCard
          title="Mora acumulada"
          value={loading.finanzas ? '—' : formatMXN(moraTotal)}
          subValue={aging ? `${moraUnits} unidades con saldo` : ''}
          delta={moraTotal > 0 ? 'Requiere atención' : 'Sin mora'}
          deltaPositive={moraTotal === 0}
        />
      ),
    },
  ];

  return (
    <div className="dash-v2 bg-[#F8F7FF] dark:bg-transparent space-y-3 p-4 pb-20">
      <div>
        <h1 className="text-[22px] font-semibold text-[#1A1A18] dark:text-theme">{greeting}</h1>
        <p className="text-[13px] text-[#888780] dark:text-theme-secondary mt-0.5">Aquí está el resumen de tu condominio</p>
        {error && <p className="text-[11px] text-[#EF9F27] mt-1">{error}</p>}
      </div>

      {/* Reservas por confirmar (solo si hay pendientes) */}
      <PendingReservationsCard
        reservations={data.pendingReservations}
        loading={loading.pendingReservations}
      />

      {/* Comprobantes por revisar (solo si hay pendientes) */}
      <PendingPaymentsCard alerts={data.alerts} loading={loading.alerts} />

      {/* Insight principal (solo si hay algo que reportar) */}
      {permissions.canReadCharge && <InsightHeroCard data={data.insight} loading={loading.insight} />}

      {/* Layout principal: Finanzas + KPIs a la izquierda, alertas/accesos/comunidad a la derecha
          (fijo: las secciones sin permiso muestran un placeholder bloqueado en vez de reacomodar el grid) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start">
        <div className="lg:col-span-8 flex flex-col gap-3">
          {permissions.canReadCharge ? (
            <FinanzasOverview
              summary={data.summary}
              aging={data.aging}
              history={data.history}
              upcoming={data.upcoming}
              loading={loading.finanzas}
              showExpenses={permissions.canReadExpense}
            />
          ) : (
            <LockedDashboardCard title="Finanzas" className="min-h-[380px]" />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {kpis.map((kpi) => (
              <div key={kpi.key}>
                {kpi.visible ? kpi.node : <LockedDashboardCard title={kpi.title} />}
              </div>
            ))}
          </div>

          {permissions.canReadAccessLog && (
            <ActivityFeed items={data.activity} loading={loading.activity} />
          )}
        </div>

        <div className="lg:col-span-4 flex flex-col gap-3">
          {permissions.canReadAlerts ? (
            <AlertList
              items={data.alerts.filter((a) => a.id !== PENDING_PAYMENTS_ALERT_ID)}
              loading={loading.alerts}
            />
          ) : (
            <LockedDashboardCard title="Alertas críticas" className="min-h-[200px]" />
          )}

          {permissions.canReadAccessLog ? (
            <TodayAccessCard
              summary={data.todaySummary}
              logs={data.recentAccessLogs}
              loadingSummary={loading.todaySummary}
              loadingLogs={loading.recentAccessLogs}
            />
          ) : (
            <LockedDashboardCard title="Accesos del día" className="min-h-[260px]" />
          )}

          {(permissions.canReadUnit || permissions.canReadReservation || permissions.canReadAnnouncement) ? (
            <CommunityCard
              unitStats={data.unitStats}
              upcomingReservations={data.upcomingReservations}
              announcements={data.latestAnnouncements}
              loadingUnits={loading.unitStats}
              loadingCommunity={loading.community}
              showUnits={permissions.canReadUnit}
              showReservation={permissions.canReadReservation}
              showAnnouncement={permissions.canReadAnnouncement}
            />
          ) : (
            <LockedDashboardCard title="Comunidad" className="min-h-[260px]" />
          )}
        </div>
      </div>
    </div>
  );
}
